import { CurrencyEnum } from '$store/models';
import { t } from '$translation/helper';

export type IncomeType = string;

export interface DailyEarningRow {
  date: number;
  currency: CurrencyEnum;
  earnings: Record<IncomeType, number>;
}

export interface StatisticsProDataset {
  rows: DailyEarningRow[];
  days: number[];
}

export interface EarningsCategoryMeta {
  type: IncomeType;
  label: string;
  currency: CurrencyEnum;
  color: string;
  pinned: boolean;
}

const KNOWN_TYPES: IncomeType[] = [
  'profit',
  'subscription_profit',
  'sold_message',
  'sold_bundle',
  'sell_telegram_post',
  'app_purchase_sold',
  'receive_tips',
  'profit_ads',
  'referral_profit',
  'referral_profit_message',
  'referral_profit_tips',
  'referral_profit_bundle',
  'referral_profit_subscription',
  'referral_profit_sponsor_subscription',
  'offer_share',
  'contest',
  'heyfans_payout',
  'battle_reward',
  'task_reward',
  'cashback',
  'top_up_welcome_bonus',
  'stake_profit',
];

const KNOWN_LABELS: Record<IncomeType, string> = {
  profit: 'Membership',
  subscription_profit: 'Sponsor subscription',
  sold_message: 'Sold message',
  sold_bundle: 'Sold bundle',
  sell_telegram_post: 'Telegram post',
  app_purchase_sold: 'In-app purchase',
  receive_tips: 'Tips',
  profit_ads: 'Ads',
  referral_profit: 'Referral',
  referral_profit_message: 'Referral · message',
  referral_profit_tips: 'Referral · tips',
  referral_profit_bundle: 'Referral · bundle',
  referral_profit_subscription: 'Referral · subscription',
  referral_profit_sponsor_subscription: 'Referral · sponsor sub',
  offer_share: 'Offer share',
  contest: 'Contest',
  heyfans_payout: 'HeyFans payout',
  battle_reward: 'Battle reward',
  task_reward: 'Task reward',
  cashback: 'Cashback',
  top_up_welcome_bonus: 'Top-up bonus',
  stake_profit: 'Staking',
};

const KNOWN_COLORS: Record<IncomeType, string> = {
  subscription_profit: '#4287f5',
  sold_message: '#ff8c42',
  sold_bundle: '#b366f5',
  app_purchase_sold: '#16a085',
  receive_tips: '#ff5b6e',
  profit_ads: '#f1c40f',
  referral_profit_message: '#ffa502',
  referral_profit_tips: '#ff7f50',
  referral_profit_bundle: '#d63aff',
  referral_profit_sponsor_subscription: '#48c9b0',
  profit: '#7ed3f7',
  sell_telegram_post: '#0088cc',
  referral_profit: '#2ecc71',
  referral_profit_subscription: '#5dade2',
  offer_share: '#85c1e9',
  contest: '#e74c3c',
  heyfans_payout: '#8e44ad',
  battle_reward: '#9b59b6',
  stake_profit: '#a569bd',
  task_reward: '#27ae60',
  cashback: '#f39c12',
  top_up_welcome_bonus: '#1abc9c',
};

const PINNED_TYPES = new Set<IncomeType>([
  'profit',
  'subscription_profit',
  'sold_message',
  'sold_bundle',
  'receive_tips',
  'stake_profit',
  'task_reward',
  'cashback',
]);

const KNOWN_ORDER = new Map<IncomeType, number>(KNOWN_TYPES.map((t, i) => [t, i]));

const humanise = (type: IncomeType): string => {
  const text = type.replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : type;
};

const generatedColor = (type: IncomeType): string => {
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = (hash * 31 + type.charCodeAt(i)) >>> 0;
  }
  return `hsl(${hash % 360}, 60%, 55%)`;
};

export const labelForType = (type: IncomeType): string =>
  t(`statistics_pro_type_${type}`, {
    defaultValue: KNOWN_LABELS[type] ?? humanise(type),
  });

export const colorForType = (type: IncomeType): string =>
  KNOWN_COLORS[type] ?? generatedColor(type);

const buildMeta = (type: IncomeType, currency: CurrencyEnum): EarningsCategoryMeta => ({
  type,
  label: labelForType(type),
  currency,
  color: colorForType(type),
  pinned: PINNED_TYPES.has(type),
});

const orderTypes = (types: IncomeType[]): IncomeType[] =>
  [...types].sort((a, b) => {
    const ia = KNOWN_ORDER.get(a);
    const ib = KNOWN_ORDER.get(b);
    if (ia !== undefined && ib !== undefined) return ia - ib;
    if (ia !== undefined) return -1;
    if (ib !== undefined) return 1;
    return a.localeCompare(b);
  });

export function getAvailableCategoriesByCurrency(
  rows: DailyEarningRow[],
): Record<CurrencyEnum, EarningsCategoryMeta[]> {
  const seen = new Map<CurrencyEnum, Set<IncomeType>>();
  for (const row of rows) {
    let set = seen.get(row.currency);
    if (!set) {
      set = new Set<IncomeType>();
      seen.set(row.currency, set);
    }
    for (const type of Object.keys(row.earnings)) {
      set.add(type);
    }
  }
  const pick = (currency: CurrencyEnum): EarningsCategoryMeta[] =>
    orderTypes(Array.from(seen.get(currency) ?? [])).map((type) =>
      buildMeta(type, currency),
    );
  return {
    [CurrencyEnum.EUR]: pick(CurrencyEnum.EUR),
    [CurrencyEnum.USD]: pick(CurrencyEnum.USD),
    [CurrencyEnum.TON]: pick(CurrencyEnum.TON),
    [CurrencyEnum.PLACE]: pick(CurrencyEnum.PLACE),
  };
}
